class ProductBottomDrawer extends HTMLElement {
  constructor() {
    super();

    this.props = {
      product: {},
      selectedVariant: {},
      variants: [],
    };

    this.state = {
      quantity: 1,
      addToCartStatus: "idle", // loading | idle
      product: {},
      selectedVariant: {},
    };

    this.isStuck = false;
    this.isSizeListExpanded = false;
  }

  connectedCallback() {
    this.initState();
    this.render(this.getRenderData());

    this.updateSizeListUI();

    this.initEventListeners();

    this.boundHandleStickyScroll = () => this.handleStickyScroll();
    window.addEventListener("scroll", this.boundHandleStickyScroll, {
      passive: true,
    });
    this.checkStickyState();

    ProductPageStore.subscribe((event, prevState) => {
      const { currentProduct, currentVariant } = event.detail;
      this.updateState({
        product: currentProduct,
        selectedVariant: currentVariant,
      });
    });

    document.addEventListener("click", this.handleDocumentClicked);
  }

  handleDocumentClicked = (e) => {
    const drawerFullContent = document.getElementById("drawer-full-content");
    if (drawerFullContent && !drawerFullContent.contains(e.target)) {
      this.isSizeListExpanded = false;
      this.updateSizeListUI();
    }
  };

  disconnectedCallback() {
    window.removeEventListener("scroll", this.boundHandleStickyScroll);
    document.removeEventListener("click", this.handleDocumentClicked);
  }

  handleStickyScroll() {
    if (this.stickyScrollTicking) return;
    this.stickyScrollTicking = true;
    requestAnimationFrame(() => {
      this.checkStickyState();
      this.stickyScrollTicking = false;
    });
  }

  checkStickyState() {
    const sentinel = document.getElementById("product-bottom-drawer-sentinel");
    if (!sentinel) return;

    const isStuck = sentinel.getBoundingClientRect().top < 0;
    if (isStuck !== this.isStuck) {
      this.isStuck = isStuck;
      // Render the isStuck-dependent content (compact vs full) first, while
      // still in normal flow, so the height we measure for the spacer below
      // matches what's about to be pinned — avoids a layout jump.
      this.updatePinning();
      this.updateState({});
    }
  }

  updatePinning() {
    const sentinel = document.getElementById("product-bottom-drawer-sentinel");
    const pinnedClasses = ["product-bottom-drawer__fixed"];
    const staticClasses = ["-mx-4"];

    if (this.isStuck) {
      const height = this.offsetHeight;
      this.classList.remove(...staticClasses);
      this.classList.add(...pinnedClasses);
      if (sentinel) sentinel.style.height = `${height}px`;
    } else {
      this.classList.remove(...pinnedClasses);
      this.classList.add(...staticClasses);
      if (sentinel) sentinel.style.height = "";
    }
  }

  initState() {
    const { currentProduct, currentVariant } = ProductPageStore.getState();
    this.state.product = currentProduct || {};
    this.state.selectedVariant = currentVariant || {};
  }

  updateState(updates) {
    this.state = { ...this.state, ...updates };
    this.render(this.getRenderData());
    this.initEventListeners();
    this.updateSizeListUI();
  }

  pollingMainCTA() {
    const interval = setInterval(() => {
      const mainCTA = this.closest("section").querySelector("block-buy-button");
      const isLoading = mainCTA.submitButton.classList.contains("loading");
      if (!isLoading) {
        this.updateState({ addToCartStatus: "idle" });
        clearInterval(interval);
      }
    }, 100);
  }

  initEventListeners() {
    const selectorTrigger = document.getElementById("selectorTrigger");
    selectorTrigger?.addEventListener("click", () => this.toggleDrawer());

    const mainCTA = document.getElementById("add-to-cart-cta");
    const CTAButtons = document.querySelectorAll(".drawer-CTA-add");

    CTAButtons.forEach((element) => {
      element.addEventListener("click", (event) => {
        event.preventDefault();

        // Assuming the form is a parent of mainCTA
        const buttons = [...mainCTA.querySelectorAll("button")];
        const button = buttons.find(
          (button) => button.getBoundingClientRect().width > 0,
        );
        button?.click();

        this.updateState({ addToCartStatus: "loading" });
        this.pollingMainCTA();
      });
    });

    const sizeItems = document.querySelectorAll("#sizes li");
    sizeItems.forEach((sizeItem) => {
      sizeItem.addEventListener("click", (event) => {
        this.handleVariantClick(event);
      });
    });
  }

  getRenderData() {
    const result = {};
    const variant = this.state.selectedVariant;

    result.isAddingToCart = this.state.addToCartStatus === "loading";
    result.discountPercentage = variant?.compare_at_price
      ? Math.round(
          ((variant.compare_at_price - variant.price) * 100) /
            variant.compare_at_price,
        )
      : 0;
    result.price = currencyFormatter.format(
      (variant?.price / 100) * this.state.quantity,
    );
    result.compareAtPrice = currencyFormatter.format(
      (variant?.compare_at_price / 100) * this.state.quantity,
    );
    result.isDiscounted = result.discountPercentage > 0;
    result.size = variant?.title?.split("(")?.[0]?.trim();

    result.isPreOrder = this.isPreOrderVariant(variant?.id);
    result.isAvailable = !!variant?.available;
    result.isSoldOut = !result.isAvailable && !result.isPreOrder;

    result.hasMultipleVariants = (this.state.product.variants || []).length > 1;

    result.variants = (this.state.product.variants || []).map((v) => ({
      ...v,
      isSelected: variant && variant.id === v.id,
      isSoldOut: !v.available && !this.isPreOrderVariant(v.id),
      priceFormatted: currencyFormatter.format(v.price / 100),
    }));

    return result;
  }

  isPreOrderVariant(variantId) {
    if (!variantId) return false;
    const { preorderVariantIds } = ProductPageStore.getState();
    return (
      Array.isArray(preorderVariantIds) &&
      preorderVariantIds.includes(String(variantId))
    );
  }

  handleVariantClick(event) {
    if (event.currentTarget.dataset.soldOut === "true") return;

    const clickedVariantId = Number(event.currentTarget.dataset.variantId);
    const clickedVariant = this.state.product.variants.find(
      (variant) => variant.id === clickedVariantId,
    );
    const blockVariantPicker = this.closest("section").querySelector(
      "block-variant-picker",
    );

    if (clickedVariant.options.length === 1) {
      const selector = this.closest("section").querySelector(
        "block-variant-picker select",
      );
      selector.value = clickedVariant.title;
    } else {
      const selectors = this.closest("section").querySelectorAll(
        "block-variant-picker select",
      );

      const [sizeSelector, mattressSelector] = selectors;
      const [sizeOption, mattressOption] = clickedVariant.options;

      sizeSelector.value = sizeOption;
      mattressSelector.value = mattressOption;
    }

    this.isSizeListExpanded = false;
    this.updateSizeListUI();

    ProductPageStore.setState({ currentVariant: clickedVariant });
  }

  chevronIcon() {
    return `
      <svg class="chevron-icon transition-transform duration-300" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.9419 6.22461C17.6978 5.98047 17.3022 5.98047 17.0581 6.22461L10 13.2827L2.9419 6.22461C2.69776 5.98047 2.30225 5.98047 2.05811 6.22461C1.81396 6.46875 1.81396 6.86426 2.05811 7.1084L9.55811 14.6084C9.68018 14.7305 9.8401 14.7915 10 14.7915C10.1603 14.7915 10.3198 14.7305 10.4419 14.6084L17.9419 7.10839C18.186 6.86425 18.186 6.46875 17.9419 6.22461Z" fill="#403833"/>
      </svg>
    `;
  }

  checkmarkIcon() {
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.6868 3.31299C14.4915 3.11768 14.1751 3.11768 13.9798 3.31299L5.66663 11.6261L2.02014 7.97965C1.82483 7.78434 1.50842 7.78434 1.31311 7.97965C1.1178 8.17496 1.1178 8.49137 1.31311 8.68668L5.31311 12.6867C5.41077 12.7843 5.5387 12.8332 5.66663 12.8332C5.79456 12.8332 5.92249 12.7843 6.02014 12.6867L14.6868 4.02002C14.8821 3.8247 14.8821 3.5083 14.6868 3.31299Z" fill="#403833"/>
      </svg>
    `;
  }

  renderVariantsList(data) {
    if (!data.variants || data.variants.length === 0) {
      return "";
    }

    return data.variants
      .map((variant) => {
        if (variant.isSoldOut) {
          return `
          <li
            class="flex justify-between items-center py-2 px-4 text-sm cursor-not-allowed hover:bg-[#E9E3DF]"
            data-variant-id="${variant.id}"
            data-sold-out="true"
          >
            <span class="line-through text-[var(--color-neutral-500)]">${variant.title}</span>
            <span class="text-xs text-[var(--color-neutral-500)]">売り切れ</span>
          </li>`;
        }

        return `
        <li
          class="flex justify-between items-center py-2 px-4 text-sm cursor-pointer hover:bg-[#E9E3DF]"
          data-variant-id="${variant.id}"
        >
          <span class="flex items-center gap-2 text-[var(--color-neutral-900)]">
            ${variant.isSelected ? this.checkmarkIcon() : ""}
            ${variant.title}
          </span>
          <span class="flex flex-col items-end">
            <span class="font-semibold text-[var(--color-neutral-900)]">${variant.priceFormatted}</span>
            <span class="text-xs text-[var(--color-neutral-700)]">税込み</span>
          </span>
        </li>`;
      })
      .join("");
  }

  render(data) {
    this.innerHTML = `
      <div id="bottom-drawer" class="bottom-drawer max-h-screen transition-all duration-300 bg-white rounded-b-lg ${this.isStuck ? "shadow-[0_6px_25px_2px_rgba(0,0,0,0.08)]" : ""}">
        <div class="px-4 py-3 flex flex-col gap-3">
          <div class="flex justify-between items-center gap-2">
            <div class="flex flex-col items-start gap-0.5 grow">
              <span class="text-xs font-semibold text-[var(--color-negative-500)] ${!data.isDiscounted ? "hidden" : ""}">-${data.discountPercentage}%</span>
              <div class="flex items-baseline gap-1">
                <span class="text-sm font-semibold text-[var(--color-neutral-900)]">${data.price}</span>
                <span class="text-sm text-[var(--color-neutral-900)] line-through ${!data.isDiscounted ? "hidden" : ""}">${data.compareAtPrice}</span>
              </div>
              <span class="text-xs text-[var(--color-neutral-700)]">税込み</span>
            </div>

            <button
              id="miniCTA"
              class="drawer-CTA-add shrink-0 min-h-[36px] px-5 rounded-full flex items-center justify-center gap-2 transition-all duration-300 ${!this.isStuck ? "hidden" : ""} ${data.isAddingToCart ? "opacity-50 cursor-not-allowed" : ""} ${data.isSoldOut ? "bg-[var(--color-neutral-200)] cursor-not-allowed" : data.isPreOrder ? "bg-[var(--color-neutral-900)]" : "bg-[var(--color-primary-400)]"}"
              ${data.isAddingToCart || data.isSoldOut ? "disabled" : ""}
            >
              <span class="text-xs font-semibold whitespace-nowrap ${data.isAddingToCart ? "hidden" : ""} ${data.isSoldOut ? "text-[var(--color-neutral-600)]" : data.isPreOrder ? "text-[var(--color-neutral-0)]" : "text-[var(--color-neutral-900)]"}">
                ${data.isSoldOut ? "売り切れ" : data.isPreOrder ? "予約注文する" : "カートに追加する"}
              </span>
              <div class="loading-overlay__spinner w-4 ${data.isAddingToCart ? "" : "hidden"}">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  role="presentation"
                  class="spinner"
                  viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle style="stroke: ${data.isPreOrder ? "white" : "#403833"};" class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </button>
          </div>

          ${
            data.hasMultipleVariants
              ? `<div id="drawer-full-content" class="relative ${!this.isStuck ? "hidden" : ""}">
            <div id="selectorTrigger" class="flex justify-between items-center bg-[#F9F7F6] rounded-lg px-4 py-3 cursor-pointer">
              <span class="text-sm text-[var(--color-neutral-900)]">${data.size}</span>
              <span class="text-[var(--color-neutral-900)]">${this.chevronIcon()}</span>
            </div>

            <div id="sizesWrapper" class="h-0 overflow-hidden transition-all duration-300 absolute top-full left-0 right-0 shadow-[0_6px_25px_2px_rgba(0,0,0,0.08)] rounded-b-lg">
              <div class="bg-white border-t border-[var(--color-neutral-500)] rounded-bl-lg rounded-br-lg">
                <ul id="sizes" class="pt-1 pb-2">
                  ${this.renderVariantsList(data)}
                </ul>
              </div>
            </div>
          </div>`
              : ""
          }

        </div>
      </div>
    `;
  }

  updateSizeListUI() {
    const { sizesWrapper, selectorTrigger, chevronIcon } =
      this.getDrawerElements();

    const isExpanded = this.isSizeListExpanded;

    selectorTrigger?.classList?.toggle("rounded-lg", !isExpanded);
    selectorTrigger?.classList?.toggle("rounded-t-lg", isExpanded);
    chevronIcon?.classList?.toggle("rotate-180", isExpanded);
    sizesWrapper?.classList?.toggle("h-0", !isExpanded);
  }

  getDrawerElements() {
    const bottomDrawer = document.getElementById("bottom-drawer");
    const sizesWrapper = document.getElementById("sizesWrapper");
    const selectorTrigger = document.getElementById("selectorTrigger");
    const chevronIcon = selectorTrigger?.querySelector(".chevron-icon");

    return { bottomDrawer, sizesWrapper, selectorTrigger, chevronIcon };
  }

  toggleDrawer() {
    this.isSizeListExpanded = !this.isSizeListExpanded;
    this.updateSizeListUI();
  }
}

customElements.define("product-bottom-drawer", ProductBottomDrawer);
